import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import RadarBeatSignal from "./RadarBeatSignal";
import RadarCaptureInspector from "./RadarCaptureInspector";
import RadarSpectrum from "./RadarSpectrum";
import RadarTrends, { SERIES } from "./RadarTrends";
import RadarWaterfall from "./RadarWaterfall";
import { Age, Fact, More, Segment, sentence } from "./radarUi";
import {
  useRadarCaptureAt,
  useRadarCaptureDetail,
  useRadarCaptureDetails,
  useRadarStream,
  useRadarWaterfall,
} from "hooks/useRadarData";
import {
  capturedAt,
  finite,
  sgtTime as sgt,
  streamMaskText,
  timeOf,
} from "utils/radarDiagnostics";
import {
  analyzeSpectrum,
  beatSpectrum,
  clipFrom,
  clipping,
  finerEcho,
} from "utils/radarSignal";
import {
  RECENT_MS,
  STREAM_WINDOWS,
  arrivalTally,
  arrivalText,
  columnCapture,
  columnSpans,
  columnTrend,
  echoListSupported,
  observedSpacing,
  rawModeText,
  readMask,
  readRawAlt,
  readRawMode,
  seqGaps,
  tallyText,
  windowText,
} from "utils/radarStream";

// Live stream (Radar analysis): everything the radar sends while the node
// streams in live mode - spectrum, beat signal, echo list, settings dump -
// with our own quick analysis, the waterfall and trends of the last 15
// minutes to 12 hours, the beat signal and the capture inspector. Streaming
// is set in Radar rich data on Node Control (radar_stream, radar_stream_s and
// the raw output mode); this panel only shows what arrived, and says so when
// nothing did lately.
// Clipping across the beat signals the page holds (firmware .56 data).
export const CLIP_SERIES = {
  key: "clip",
  title: "Clipping (beat signals)",
  unit: "%",
  digits: 1,
  hint: "Share of beat-signal samples at the receiver's rails: it saturates on a strong close echo. From the beat signals of the last 10 minutes.",
  color: "#b07a10",
};
// A beat signal counts for a spectrum shown when it came within a minute.
const BESIDE_MS = 60000;

// The newest answer to the echo-list query among loaded capture details:
// {ms, reply}, or null.
function newestEchoReply(details) {
  let best = null;
  for (const data of details) {
    const reply = data?.context?.peaks_reply;
    const ms = timeOf(capturedAt(data));
    if (ms === null || echoListSupported(reply) === null) continue;
    if (!best || ms > best.ms) best = { ms, reply };
  }
  return best;
}

export default function RadarStreamPanel({
  mac,
  settings = null,
  refresh = 0,
}) {
  const reported = settings?.reported || {};
  const reportedMask = readMask(reported.radar_stream);
  const reportedSeconds = finite(reported.radar_stream_s);
  const allowed = Array.isArray(settings?.allowed_keys)
    ? settings.allowed_keys
    : null;
  const modeAllowed = !!allowed?.includes("radar_raw_mode");
  const reportedMode = readRawMode(reported.radar_raw_mode);
  const altAllowed = !!allowed?.includes("radar_raw_alt");
  const reportedAlt = readRawAlt(reported.radar_raw_alt);
  const [range, setRange] = useState("near");
  const [minutes, setMinutes] = useState(60);
  const [selection, setSelection] = useState(null);
  const [echoReply, setEchoReply] = useState(null);
  const detailCache = useRef(new Map());
  const clipCache = useRef(new Map());
  const atCache = useRef(new Map());
  const trendCache = useRef(new WeakMap());

  // ---- Data: followed every 5 s while captures keep arriving ----
  const cadence = reportedMask > 0 && reportedSeconds ? reportedSeconds : 30;
  const stream = useRadarStream(mac, {
    keepFastWithinMs: Math.max(60000, 3 * cadence * 1000),
    refresh,
  });
  const wf = useRadarWaterfall(mac, minutes, {
    refresh,
    captures: stream.captures,
    offset: stream.offset,
  });
  const nowMs = Date.now() + stream.offset;
  const columns = useMemo(() => wf.data?.columns || [], [wf.data]);
  const spans = useMemo(() => columnSpans(columns), [columns]);
  const latest = stream.captures.length
    ? stream.captures[stream.captures.length - 1]
    : null;
  const lastColumn = columns.length ? columns[columns.length - 1] : null;
  // A picked column, else the newest column while no capture of the last ten
  // minutes is loaded.
  const target = useMemo(
    () =>
      selection ||
      (!latest && lastColumn ? { key: lastColumn.t, ms: lastColumn.ms } : null),
    [selection, latest, lastColumn],
  );
  const tolerance = Math.max(60000, 2 * (spans[0]?.typical || 30000));
  const at = useRadarCaptureAt(mac, target, tolerance, atCache);
  const targetColumn = useMemo(
    () => (target ? columns.find((c) => c.t === target.key) || null : null),
    [columns, target],
  );
  const standIn = useMemo(
    () =>
      at.missing && targetColumn && wf.data
        ? columnCapture(targetColumn, wf.data)
        : null,
    [at.missing, targetColumn, wf.data],
  );
  const shown = target ? at.capture || standIn : latest;
  const detail = useRadarCaptureDetail(shown?.id ?? null, 0, detailCache);
  // The newest capture's details too, while another one is inspected: its
  // answer to the echo-list query decides the Echo list toggle.
  const latestDetail = useRadarCaptureDetail(
    latest && latest.id !== shown?.id ? latest.id : null,
    0,
    detailCache,
  );
  useEffect(() => {
    const found = newestEchoReply([detail.data, latestDetail.data]);
    if (found) setEchoReply((old) => (!old || found.ms > old.ms ? found : old));
  }, [detail.data, latestDetail.data]);
  const echoUnsupported = echoListSupported(echoReply?.reply) === false;
  const analysis = useMemo(
    () => (shown ? analyzeSpectrum(shown) : null),
    [shown],
  );
  // The beat signal for the capture shown: its own, or, with alternation, the
  // nearest capture with one within a minute. It gives a finer position than
  // the radar's unwindowed spectrum, and says whether the receiver clips.
  const beatCapture = useMemo(() => {
    if (!shown || shown.has_adc) return shown;
    const ms = timeOf(shown.t);
    let best = null;
    for (const c of stream.captures) {
      const gap = Math.abs((timeOf(c?.t) ?? Infinity) - ms);
      if (c?.has_adc && c.id !== null && gap <= BESIDE_MS)
        if (!best || gap < best.gap) best = { c, gap };
    }
    return best ? best.c : shown;
  }, [shown, stream.captures]);
  const besideDetail = useRadarCaptureDetail(
    beatCapture && beatCapture !== shown ? beatCapture.id : null,
    0,
    detailCache,
  );
  const beatDetail = beatCapture === shown ? detail : besideDetail;
  const beatSamples = useMemo(
    () =>
      beatCapture?.has_adc && Array.isArray(beatDetail?.data?.adc)
        ? beatDetail.data.adc.map(Number).filter(Number.isFinite)
        : null,
    [beatCapture, beatDetail],
  );
  // The server's clipping measure (clip) when it has one, else ours.
  const beatClip = useMemo(
    () => (beatSamples ? clipFrom(beatDetail?.data, beatSamples) : null),
    [beatDetail, beatSamples],
  );
  const finer = useMemo(() => {
    const near = analysis?.chosen?.bin ?? analysis?.pick?.bin;
    if (!beatSamples || near === null || near === undefined) return null;
    const found = finerEcho(beatSpectrum(beatSamples), near);
    return found
      ? {
          bin: found.bin,
          mm: found.bin * analysis.step - analysis.cal,
          source: beatCapture !== shown ? `of ${sgt(beatCapture.t)}` : null,
        }
      : null;
  }, [beatSamples, analysis, beatCapture, shown]);
  // Clipping across the beat signals held (the last ten minutes): the
  // server's clip_pct on each stream row; from a server before it, measured
  // here from the stored records of the newest 20.
  const beatIds = useMemo(
    () =>
      stream.captures
        .filter(
          (c) =>
            c?.has_adc &&
            c.clip_pct === undefined &&
            c.id !== null &&
            c.id !== undefined,
        )
        .slice(-20)
        .map((c) => c.id),
    [stream.captures],
  );
  const beatDetails = useRadarCaptureDetails(beatIds, clipCache);
  const clipPoints = useMemo(
    () =>
      stream.captures
        .map((c) => {
          if (!c?.has_adc) return null;
          const adc = beatDetails.get(String(c.id))?.data?.adc;
          const pct =
            c.clip_pct !== undefined
              ? finite(c.clip_pct)
              : Array.isArray(adc)
                ? (clipping(adc)?.pct ?? null)
                : null;
          return pct === null ? null : { ms: timeOf(c.t), clip: pct };
        })
        .filter(Boolean),
    [stream.captures, beatDetails],
  );
  const points = useMemo(
    () =>
      columns.map((c) => {
        let p = trendCache.current.get(c);
        if (!p) {
          p = columnTrend(c, wf.data);
          trendCache.current.set(c, p);
        }
        return p;
      }),
    [columns, wf.data],
  );
  const windowTo = Math.max(
    nowMs,
    spans.length ? spans[spans.length - 1].end : -Infinity,
  );
  const trendPoints = useMemo(
    () =>
      clipPoints.length
        ? [...points, ...clipPoints].sort((a, b) => a.ms - b.ms)
        : points,
    [points, clipPoints],
  );

  // ---- Status ----
  const lastMs = latest ? timeOf(latest.t) : null;
  const lastAny = Math.max(lastMs ?? -Infinity, lastColumn?.ms ?? -Infinity);
  const flowing =
    lastMs !== null && nowMs - lastMs < Math.max(60000, 3 * cadence * 1000);
  const recent = stream.captures.filter(
    (c) => timeOf(c.t) >= nowMs - RECENT_MS,
  );
  const gaps = seqGaps(recent);
  // Expected captures: at the spacing observed (five captures or more), else
  // at the setting.
  const spacing = observedSpacing(recent);
  const tally = arrivalTally(recent);
  const streamMissing =
    stream.error?.status === 404 || wf.error?.status === 404;
  // Streamed lately: a capture in the last ten minutes or in the window.
  const loaded = stream.loaded && (!!wf.data || !!wf.error);
  const lately = !!latest || columns.length > 0;
  const [chip, tone] = flowing
    ? [`Streaming · every ${cadence} s`, "is-running"]
    : lately
      ? ["Not streaming now", "is-off"]
      : ["No live stream lately", "is-off"];
  const reportedText =
    reportedMask === null
      ? "Not reported yet"
      : reportedMask === 0
        ? "Not streaming"
        : `${sentence(streamMaskText(reportedMask))}${reportedSeconds ? ` every ${reportedSeconds} s` : ""} (in live mode only)`;

  return (
    <section
      className="field-panel field-stream"
      aria-labelledby="field-stream-title"
    >
      <div className="field-panel-head">
        <div>
          <h2 id="field-stream-title">Live stream</h2>
          <p>
            Everything the radar sends while it streams in live mode, with our
            analysis
          </p>
        </div>
        <span className={`field-state ${tone}`} role="status">
          {tone === "is-running" && (
            <span className="field-live-dot" aria-hidden="true" />
          )}
          {chip}
        </span>
      </div>
      {streamMissing ? (
        <div className="field-stream-section is-first">
          <div className="field-notice">
            This server does not provide the radar stream yet; captures and the
            waterfall need the updated backend.
          </div>
        </div>
      ) : !loaded ? (
        <div className="field-stream-section is-first">
          <p className="field-caption">Loading the live stream…</p>
        </div>
      ) : !lately ? (
        <div className="field-stream-section is-first">
          {stream.error && (
            <div className="field-alert" role="alert">
              The stream could not load: {stream.error.message}. The view keeps
              what it has and tries again.
            </div>
          )}
          <div className="field-radar-empty">
            No live stream in the last {windowText(minutes)}. The radar streams
            only while the node is in live mode, on external power: start{" "}
            <Link to="/node-control#field-live-title">
              Live mode on Node Control
            </Link>{" "}
            and turn streaming on in its Radar rich data card. Rich readings and
            bursts, above, bring the same data without live mode.
          </div>
          <Segment
            label="Look back"
            options={STREAM_WINDOWS}
            value={minutes}
            onChange={setMinutes}
          />
        </div>
      ) : (
        <>
          <div className="field-stream-grid">
            <div className="field-stream-main">
              {stream.error && (
                <div className="field-alert" role="alert">
                  The stream could not load: {stream.error.message}. The view
                  keeps what it has and tries again.
                </div>
              )}
              <div className="field-stream-toolbar">
                <div>
                  <h3 className="field-radar-heading">
                    {selection ? "Inspecting a capture" : "Latest capture"}
                  </h3>
                  <p className="field-stream-when">
                    {shown
                      ? `${sgt(shown.t)}${shown.fromWaterfall ? " · from the waterfall (0.5 dB steps; the capture is no longer stored)" : ""}`
                      : "—"}
                  </p>
                </div>
                <div className="field-stream-toolbar-actions">
                  {selection && (
                    <button
                      type="button"
                      className="field-button"
                      onClick={() => setSelection(null)}
                    >
                      Back to latest
                    </button>
                  )}
                  <Segment
                    label="Range"
                    options={[
                      ["near", "0–2 m"],
                      ["full", "Full range"],
                    ]}
                    value={range}
                    onChange={setRange}
                  />
                </div>
              </div>
              {at.error ? (
                <div className="field-alert" role="alert">
                  The capture could not load: {at.error}
                </div>
              ) : shown ? (
                <RadarSpectrum
                  capture={shown}
                  analysis={analysis}
                  range={range}
                  unit={stream.unit || wf.data?.dbUnit || null}
                  echoAnswered={
                    echoListSupported(detail.data?.context?.peaks_reply) ===
                    true
                  }
                  finer={finer}
                  clip={beatClip?.clipped ? beatClip : null}
                />
              ) : target && at.loading ? (
                <p className="field-caption">
                  Loading the capture of{" "}
                  {sgt(new Date(target.ms).toISOString())}…
                </p>
              ) : (
                <div className="field-radar-empty">
                  No capture in the last ten minutes: pick a column in the
                  waterfall below to inspect an earlier one.
                </div>
              )}
            </div>
            <div className="field-stream-side">
              <div role="group" aria-label="Stream status">
                <dl className="field-facts field-stream-facts">
                  <Fact label="The node streams">{reportedText}</Fact>
                  <Fact label="Last capture">
                    <Age ms={lastAny} offset={stream.offset} />
                  </Fact>
                  <Fact label="Captures, last 10 min">
                    {recent.length.toLocaleString()}
                    {flowing
                      ? spacing
                        ? ` · about ${Math.round(RECENT_MS / spacing)} expected (every ~${Math.round(spacing / 1000)} s observed)`
                        : ` · about ${Math.round(600 / cadence)} expected`
                      : ""}
                    {gaps ? ` · ${gaps} missing by sequence number` : ""}
                  </Fact>
                </dl>
                {(modeAllowed ||
                  reportedMode !== null ||
                  altAllowed ||
                  reportedAlt !== null ||
                  tally.length > 0) && (
                  <More label="Modes and packets">
                    <dl className="field-facts field-stream-facts">
                      {(modeAllowed || reportedMode !== null) && (
                        <Fact label="Raw output mode">
                          {reportedMode !== null
                            ? rawModeText(reportedMode)
                            : "Not reported yet"}
                        </Fact>
                      )}
                      {(altAllowed || reportedAlt !== null) && (
                        <Fact label="Alternating spectrum and beat signal">
                          {reportedAlt === null
                            ? "Not reported yet"
                            : reportedAlt
                              ? "On: modes 1 and 2 in turn"
                              : "Off"}
                        </Fact>
                      )}
                    </dl>
                    {tally.length > 0 && (
                      <div
                        className="field-stream-arrivals"
                        role="group"
                        aria-labelledby="field-stream-arrived"
                      >
                        <h4
                          id="field-stream-arrived"
                          className="field-stream-subhead"
                        >
                          What arrived, last 10 min
                        </h4>
                        <ul className="field-stream-tally">
                          {tally.map((g) => (
                            <li key={g.mode ?? "none"}>{tallyText(g)}</li>
                          ))}
                        </ul>
                        <p className="field-caption">
                          Latest capture: {arrivalText(latest)}. Packets of each
                          type in a capture's raw-output window, as the node
                          counted them.
                        </p>
                      </div>
                    )}
                  </More>
                )}
              </div>
              <p className="field-caption" style={{ margin: 0 }}>
                What streams, how often and in which raw output mode is set in{" "}
                <Link to="/node-control">Radar rich data on Node Control</Link>;
                streaming runs only in live mode.
              </p>
            </div>
          </div>
          <div
            className="field-stream-section"
            role="group"
            aria-labelledby="field-stream-waterfall"
          >
            <div className="field-stream-toolbar">
              <div>
                <h3 id="field-stream-waterfall" className="field-radar-heading">
                  Waterfall
                </h3>
                <p className="field-caption" style={{ margin: 0 }}>
                  Every spectrum of the last {windowText(minutes)}; click a
                  column to inspect that capture above.
                </p>
              </div>
              <Segment
                label="Window"
                options={STREAM_WINDOWS}
                value={minutes}
                onChange={setMinutes}
              />
            </div>
            {wf.error && wf.error.status !== 404 && (
              <div className="field-alert" role="alert">
                The waterfall could not load: {wf.error.message}
              </div>
            )}
            {wf.data ? (
              <RadarWaterfall
                data={wf.data}
                minutes={minutes}
                range={range}
                now={nowMs}
                selectedKey={selection?.key ?? null}
                onSelect={(column) =>
                  setSelection(column ? { key: column.t, ms: column.ms } : null)
                }
              />
            ) : (
              !wf.error && (
                <p className="field-caption">Loading the waterfall…</p>
              )
            )}
          </div>
          {wf.data && (
            <div
              className="field-stream-section"
              role="group"
              aria-labelledby="field-stream-trends"
            >
              <h3 id="field-stream-trends" className="field-radar-heading">
                Trends over the last {windowText(minutes)}
              </h3>
              <RadarTrends
                points={trendPoints}
                from={windowTo - minutes * 60000}
                to={windowTo}
                selectedMs={selection?.ms ?? null}
                series={clipPoints.length ? [...SERIES, CLIP_SERIES] : SERIES}
                className={clipPoints.length ? "is-five" : ""}
              />
            </div>
          )}
          <div
            className="field-stream-section"
            role="group"
            aria-labelledby="field-stream-beat"
          >
            <h3 id="field-stream-beat" className="field-radar-heading">
              Beat signal
            </h3>
            {shown ? (
              <RadarBeatSignal
                capture={beatCapture}
                detail={beatDetail}
                spectrum={shown}
                range={range}
                modes={modeAllowed || altAllowed}
              />
            ) : (
              <p className="field-caption">
                The beat signal of the capture shown above appears here.
              </p>
            )}
          </div>
          <div
            className="field-stream-section"
            role="group"
            aria-labelledby="field-stream-inspector"
          >
            <h3 id="field-stream-inspector" className="field-radar-heading">
              Capture inspector
            </h3>
            <RadarCaptureInspector
              mac={mac}
              capture={shown}
              detail={detail}
              analysis={analysis}
              echoUnsupported={echoUnsupported}
            />
          </div>
        </>
      )}
    </section>
  );
}
